import { action } from '@reatom/framework'

import { Data, pipe, Result } from '@blade/std-lib'
import { CurrencyModel } from '@root/entities/currency/model'
import { Network } from '@root/entities/network'
import { solToLamports } from '@root/entities/network/model/solana'
import { PathfinderUtil, Quote } from '@root/entities/quote/model'
import {
  TradeSettingsSolRpc,
  type TradeSettings,
} from '@root/entities/trade-settings/model'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { solSelectedRpcAtom } from '@root/features/trade/trade-settings/atoms'
import { environmentAtom, UnknownError } from '@root/shared'
import { ApiError, apiRequest } from '@root/shared/api'
import { ServicesErrorCode } from '@root/shared/api/api-error-code'

import type { PairAddress } from '@root/entities/pair'
import type { Provider } from '@root/entities/provider'
import type { InviteCode } from '@root/entities/referral'
import type { WalletModel } from '@root/entities/wallet'
import type BigNumber from 'bignumber.js'

type FetchQuoteArgs = {
  amount: BigNumber
  currencyIn: CurrencyModel
  currencyOut: CurrencyModel
  type: Quote.QuoteType
  poolAddress: PairAddress
  providerId: Provider.Id
  network: Network.Network
  wallet: WalletModel | undefined
  prepareSwap?: boolean
  inviteCode: InviteCode | undefined
  settings: TradeSettings.TradeSettings
}

export class NotEnoughLiquidityError extends Data.TaggedError(
  'NotEnoughLiquidityError',
) {}

export const fetchQuoteRequestAction = action(
  async (ctx, args: FetchQuoteArgs) => {
    const { pathfinderEnabled } = ctx.get(environmentAtom)
    const result = await ctx.schedule(() => {
      if (pathfinderEnabled) {
        return fetchPathfinderQuoteRequestAction(ctx, args)
      }
      return fetchTradeApiQuoteRequestAction(ctx, args)
    })
    return Result.unify(result)
  },
  'fetchQuoteRequestAction',
)

export const fetchTradeApiQuoteRequestAction = action(
  async (ctx, args: FetchQuoteArgs) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const {
      amount,
      currencyIn,
      currencyOut,
      network,
      poolAddress,
      providerId,
      type,
      wallet,
      prepareSwap,
      settings,
    } = args

    const quoteResult = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.services.servicesQuoteCreate, [
        {
          network: Network.toDto(network),
          amount: amount.toString(),
          baseToken: currencyIn.address,
          quoteToken: currencyOut.address,
          pairAddress: poolAddress,
          provider: providerId,
          slippage: settings.slippage,
          type: type,
          walletAddress: wallet?.address,
          walletId: wallet?.id,
          prepareSwap,
        },
      ]),
    )

    const mappedResult = pipe(
      quoteResult,
      Result.map((it) => {
        return Quote.fromDto(it, network)
      }),
      Result.mapError((it) => {
        if (ApiError.isOfCode(ServicesErrorCode.NotEnoughLiquidity)(it)) {
          return new NotEnoughLiquidityError()
        }
        return it
      }),
    )
    return mappedResult
  },
  'fetchTradeApiQuoteRequestAction',
)

export const fetchPathfinderQuoteRequestAction = action(
  async (ctx, args: FetchQuoteArgs) => {
    const { pathfinderApiClient } = ctx.get(dependenciesAtom)
    const { amount, currencyIn, currencyOut, wallet, inviteCode, settings } =
      args

    if (settings.network !== Network.Solana) {
      return Result.fail(UnknownError.fromUnknown('Network not supported'))
    }

    const amountIn =
      CurrencyModel.convertAmountToBigInt(currencyIn)(amount).toNumber()
    const slippageBps = settings.slippage * 100

    const tokenIn = PathfinderUtil.getPathfinderAddress(currencyIn)
    const tokenOut = PathfinderUtil.getPathfinderAddress(currencyOut)

    const tip = solToLamports(settings.jitoTip)
    const priority = solToLamports(settings.priorityFee)
    const rpc = ctx.get(solSelectedRpcAtom)

    const quoteResult = await ctx.schedule(() =>
      apiRequest(ctx, pathfinderApiClient.pathfinderd.quotePost, [
        {
          token_in: tokenIn,
          token_out: tokenOut,
          amount_in: amountIn,
          taker: wallet?.address,
          slippage: slippageBps,
          referral_code: inviteCode,
          priority,
          tip,
          compute_unit: Quote.COMPUTE_UNIT,
          sender_type: TradeSettingsSolRpc.toPathfinderSenderType(rpc),
        },
      ]),
    )

    const mappedResult = pipe(
      quoteResult,
      Result.mapError(ApiError.fallbackToUnknown),
      Result.flatMap((it) => {
        const quote = Quote.fromPathfinderDto(it, currencyIn, currencyOut)

        if (!quote) {
          // TODO: Type this
          return Result.fail(UnknownError.fromUnknown('Quote not found'))
          // return Result.fail(new QuoteNotFoundError())
        }
        return Result.succeed(quote)
      }),
    )

    return mappedResult
  },
  'fetchPathfinderQuoteRequestAction',
)
